import { useState, useEffect } from 'react';
import type { RoomLayout, Questionnaire } from '../../../data-transformer/index.ts';
import type { ValidateResult } from 'discriminator';
import type { Zone } from '../../../zoning/index.ts';
import { ROOM_TEMPLATES, DEFAULT_QUESTIONNAIRE } from '../presets.ts';

export interface HistoryEntry {
  roomData: RoomLayout;
  questionnaire: Questionnaire;
  prompt: string;
  generatedScript: string;
  validationResult: ValidateResult | null;
  geminiCalls: any[];
  logs: any[];
  model: string;
  aiMessage: string;
  searchLogs: any[];
  zones?: Zone[];
  zoningExplanation?: string;
}

export function useRoomLayout() {
  const [roomData, setRoomData] = useState<RoomLayout>(ROOM_TEMPLATES.empty);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>(DEFAULT_QUESTIONNAIRE);
  const [prompt, setPrompt] = useState('Arrange the desk facing the room entrance, ensure the chair is behind it, and position the cabinet neatly against the wall.');
  const [model, setModel] = useState('gemini-3.5-flash-lite');
  const [aiMessage, setAiMessage] = useState('');
  const [backendUrl, setBackendUrl] = useState(() => {
    if (typeof window !== 'undefined') {
      if (window.location.port === '3000') {
        return 'http://localhost:5001';
      }
      return window.location.origin;
    }
    return 'http://localhost:5001';
  });
  
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Pipeline outputs
  const [logs, setLogs] = useState<any[]>([]);
  const [generatedScript, setGeneratedScript] = useState('');
  const [validationResult, setValidationResult] = useState<ValidateResult | null>(null);
  const [geminiCalls, setGeminiCalls] = useState<any[]>([]);
  const [searchLogs, setSearchLogs] = useState<any[]>([]);

  // Zoning Service states
  const [zones, setZones] = useState<Zone[]>([]);
  const [isZoning, setIsZoning] = useState<boolean>(false);
  const [zoningPrompt, setZoningPrompt] = useState<string>('Rozděl místnost na hlavní obývací prostor s pohovkou a TV stěnou v levé části a jídelní zónu se stolem pro 4 v pravé části.');
  const [zoningModel, setZoningModel] = useState<string>('gemini-3.7-flash');
  const [zoningExplanation, setZoningExplanation] = useState<string>('');
  const [zoningErrorMessage, setZoningErrorMessage] = useState<string>('');
  const [showZonesInPreview, setShowZonesInPreview] = useState<boolean>(true);
  const [focusedZoneId, setFocusedZoneId] = useState<string | null>(null);
  
  // Checking active validation state
  const [isValidating, setIsValidating] = useState(false);

  // DB Rooms listing & selection
  const [dbRooms, setDbRooms] = useState<Array<{ id: string; name: string; isEmpty: boolean }>>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string>('empty');

  // Connection status checks
  const [backendHealthy, setBackendHealthy] = useState<boolean | null>(null);

  // Undo/Redo State History Stack
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Initialize history on first mount
  useEffect(() => {
    if (history.length === 0) {
      const initialEntry: HistoryEntry = {
        roomData: ROOM_TEMPLATES.empty,
        questionnaire: DEFAULT_QUESTIONNAIRE,
        prompt: 'Arrange the desk facing the room entrance, ensure the chair is behind it, and position the cabinet neatly against the wall.',
        generatedScript: '',
        validationResult: null,
        geminiCalls: [],
        logs: [],
        model: 'gemini-3.5-flash-lite',
        aiMessage: '',
        searchLogs: [],
        zones: [],
        zoningExplanation: ''
      };
      setHistory([initialEntry]);
      setHistoryIndex(0);
    }
  }, []);

  // Poll backend health & fetch rooms list when healthy
  useEffect(() => {
    fetch(`${backendUrl}/api/health`)
      .then(res => res.json())
      .then(data => {
        const healthy = data.status === 'Healthy';
        setBackendHealthy(healthy);
        if (healthy) {
          fetch(`${backendUrl}/api/rooms`)
            .then(r => r.json())
            .then(rooms => {
              if (Array.isArray(rooms)) {
                setDbRooms(rooms);
              }
            })
            .catch(e => console.error('Failed to fetch rooms list:', e));
        }
      })
      .catch(() => setBackendHealthy(false));
  }, [backendUrl]);

  // Debounced auto-validation triggered on layout/config changes
  useEffect(() => {
    const timer = setTimeout(async () => {
      // Avoid auto-validating when full generation is running
      if (status === 'loading') return;

      setIsValidating(true);
      try {
        const response = await fetch(`${backendUrl}/api/validate-layout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            roomData,
            questionnaire
          })
        });

        if (!response.ok) {
          throw new Error(`Validation status check failed: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          setValidationResult(data.validation);
        }
      } catch (err) {
        console.warn('Auto-validation check failed:', err);
      } finally {
        setIsValidating(false);
      }
    }, 450); // 450ms debounce window

    return () => clearTimeout(timer);
  }, [roomData, questionnaire, backendUrl, status]);

  // Push new snapshot to the history stack
  const pushToHistory = (
    newRoom: RoomLayout,
    newQuest: Questionnaire,
    newPr: string,
    newScript: string,
    newVal: ValidateResult | null,
    newCalls: any[],
    newLogs: any[],
    newModel: string,
    newAiMessage: string,
    newSearchLogs: any[],
    newZones?: Zone[],
    newZoningExpl?: string
  ) => {
    const targetZones: Zone[] = newZones !== undefined
      ? newZones
      : (newRoom.metadata?.zones && Array.isArray(newRoom.metadata.zones) ? newRoom.metadata.zones : []);
    const targetZoningExpl: string = newZoningExpl !== undefined
      ? newZoningExpl
      : (newRoom.metadata?.zoningExplanation || '');

    // Anchor zones directly into room metadata
    const roomWithZones: RoomLayout = {
      ...newRoom,
      metadata: {
        ...newRoom.metadata,
        zones: targetZones,
        zoningExplanation: targetZoningExpl
      }
    };

    setHistory(prev => {
      // Truncate any forward history if we navigated back and then generated new path
      const cleanHistory = prev.slice(0, historyIndex + 1);
      const newEntry: HistoryEntry = {
        roomData: JSON.parse(JSON.stringify(roomWithZones)), // deep copy to prevent mutations
        questionnaire: JSON.parse(JSON.stringify(newQuest)),
        prompt: newPr,
        generatedScript: newScript,
        validationResult: newVal,
        geminiCalls: newCalls,
        logs: newLogs,
        model: newModel,
        aiMessage: newAiMessage,
        searchLogs: JSON.parse(JSON.stringify(newSearchLogs)),
        zones: JSON.parse(JSON.stringify(targetZones)),
        zoningExplanation: targetZoningExpl
      };
      return [...cleanHistory, newEntry];
    });
    setHistoryIndex(prev => prev + 1);
  };

  // Sync manual input values directly in the current history index
  const updateCurrentHistoryEntry = (updatedRoom: RoomLayout, updatedQuest: Questionnaire, updatedPrompt: string, updatedModel: string, updatedAiMessage?: string, updatedZones?: Zone[], updatedZoningExpl?: string) => {
    setHistory(prev => {
      if (historyIndex < 0 || historyIndex >= prev.length) return prev;

      const targetZones: Zone[] = updatedZones !== undefined
        ? updatedZones
        : (updatedRoom.metadata?.zones && Array.isArray(updatedRoom.metadata.zones) ? updatedRoom.metadata.zones : (prev[historyIndex].zones || []));
      const targetZoningExpl: string = updatedZoningExpl !== undefined
        ? updatedZoningExpl
        : (updatedRoom.metadata?.zoningExplanation || prev[historyIndex].zoningExplanation || '');

      const roomWithZones: RoomLayout = {
        ...updatedRoom,
        metadata: {
          ...updatedRoom.metadata,
          zones: targetZones,
          zoningExplanation: targetZoningExpl
        }
      };

      const copy = [...prev];
      copy[historyIndex] = {
        ...copy[historyIndex],
        roomData: JSON.parse(JSON.stringify(roomWithZones)),
        questionnaire: JSON.parse(JSON.stringify(updatedQuest)),
        prompt: updatedPrompt,
        model: updatedModel,
        aiMessage: typeof updatedAiMessage !== 'undefined' ? updatedAiMessage : copy[historyIndex].aiMessage,
        zones: JSON.parse(JSON.stringify(targetZones)),
        zoningExplanation: targetZoningExpl
      };
      return copy;
    });
  };

  // Undo (Zpět)
  const handleUndo = () => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      const entry = history[prevIndex];
      const entryZones: Zone[] = entry.roomData.metadata?.zones || entry.zones || [];
      const entryExpl: string = entry.roomData.metadata?.zoningExplanation || entry.zoningExplanation || '';
      
      setRoomData(entry.roomData);
      setQuestionnaire(entry.questionnaire);
      setPrompt(entry.prompt);
      setGeneratedScript(entry.generatedScript);
      setValidationResult(entry.validationResult);
      setGeminiCalls(entry.geminiCalls);
      setLogs(entry.logs);
      setModel(entry.model);
      setAiMessage(entry.aiMessage);
      setSearchLogs(entry.searchLogs || []);
      setZones(entryZones);
      setZoningExplanation(entryExpl);
    }
  };

  // Redo (Dopředu)
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      const entry = history[nextIndex];
      const entryZones: Zone[] = entry.roomData.metadata?.zones || entry.zones || [];
      const entryExpl: string = entry.roomData.metadata?.zoningExplanation || entry.zoningExplanation || '';
      
      setRoomData(entry.roomData);
      setQuestionnaire(entry.questionnaire);
      setPrompt(entry.prompt);
      setGeneratedScript(entry.generatedScript);
      setValidationResult(entry.validationResult);
      setGeminiCalls(entry.geminiCalls);
      setLogs(entry.logs);
      setModel(entry.model);
      setAiMessage(entry.aiMessage);
      setSearchLogs(entry.searchLogs || []);
      setZones(entryZones);
      setZoningExplanation(entryExpl);
    }
  };

  // Handle changing DB Room source dropdown
  const handleRoomSelect = async (roomId: string) => {
    setSelectedRoomId(roomId);
    if (roomId === 'empty') {
      const emptyLayout = ROOM_TEMPLATES.empty;
      setRoomData(emptyLayout);
      setZones([]);
      setZoningExplanation('');
      setFocusedZoneId(null);
      pushToHistory(emptyLayout, questionnaire, prompt, '', null, [], [], model, '', [], [], '');
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    try {
      const response = await fetch(`${backendUrl}/api/rooms/${encodeURIComponent(roomId)}`);
      if (!response.ok) {
        throw new Error(`Failed to retrieve room from database (status ${response.status})`);
      }
      const data = await response.json() as RoomLayout;
      const roomZones: Zone[] = data.metadata?.zones && Array.isArray(data.metadata.zones) ? data.metadata.zones : [];
      const roomZoningExpl: string = data.metadata?.zoningExplanation || '';
      
      setRoomData(data);
      setZones(roomZones);
      setZoningExplanation(roomZoningExpl);
      setFocusedZoneId(null);
      setStatus('idle');
      pushToHistory(data, questionnaire, prompt, '', null, [], [], model, '', [], roomZones, roomZoningExpl);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  };

  // Save the current layout into the DB test room ALLOWED_ROOM_PATCH_ID and redirect
  const handleSaveRoom = async () => {
    setStatus('loading');
    setErrorMessage('');
    try {
      const response = await fetch(`${backendUrl}/api/rooms/019ef3c1-e54d-d9a9-17d0-91154620d070`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roomData
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to patch room layout in database (status ${response.status})`);
      }

      const data = await response.json();
      if (data.success) {
        setStatus('success');
        // Open the graphical editor page in a new tab
        const viewerUrl = process.env.GRAPHICS_VIEWER_URL || 'https://archie.iwory.cz/rooms/019ef3c1-e54d-d9a9-17d0-91154620d070';
        window.open(viewerUrl, '_blank');

        // Retrieve fresh database room list
        const roomsRes = await fetch(`${backendUrl}/api/rooms`);
        const rooms = await roomsRes.json();
        if (Array.isArray(rooms)) {
          setDbRooms(rooms);
        }
      } else {
        throw new Error(data.error || 'Failed to update test room');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  };

  const handleFieldChange = (field: keyof RoomLayout, val: any) => {
    const updated = {
      ...roomData,
      [field]: val
    };
    setRoomData(updated);
    updateCurrentHistoryEntry(updated, questionnaire, prompt, model);
  };

  const handleQuestionnaireChange = (field: keyof Questionnaire, val: any) => {
    const updated = {
      ...questionnaire,
      [field]: val
    };
    setQuestionnaire(updated);
    updateCurrentHistoryEntry(roomData, updated, prompt, model);
  };

  const handlePromptChange = (newPrompt: string) => {
    setPrompt(newPrompt);
    updateCurrentHistoryEntry(roomData, questionnaire, newPrompt, model);
  };

  const handleModelChange = (newModel: string) => {
    setModel(newModel);
    updateCurrentHistoryEntry(roomData, questionnaire, prompt, newModel);
  };

  // Drag and drop JSON file uploads
  const handleRoomDataUpload = (uploadedData: RoomLayout) => {
    const uploadedZones: Zone[] = uploadedData.metadata?.zones && Array.isArray(uploadedData.metadata.zones) ? uploadedData.metadata.zones : [];
    const uploadedZoningExpl: string = uploadedData.metadata?.zoningExplanation || '';

    setRoomData(uploadedData);
    setZones(uploadedZones);
    setZoningExplanation(uploadedZoningExpl);
    setFocusedZoneId(null);
    setSelectedRoomId('uploaded');
    pushToHistory(uploadedData, questionnaire, prompt, '', null, [], [], model, '', [], uploadedZones, uploadedZoningExpl);
  };

  // Apply visual error repairs proposed by the Discriminator
  const handleSolveError = (repairInstructions: string, impactedObjectId?: string, rule?: any) => {
    if (!repairInstructions) return;
    
    let targetPrompt = '';
    const item = impactedObjectId ? roomData.items.find(i => i.itemId === impactedObjectId) : null;
    
    if (item) {
      targetPrompt = `Fix layout: For the object "${item.label}" (ID: ${impactedObjectId}), please apply: ${repairInstructions}`;
    } else if (impactedObjectId) {
      targetPrompt = `Fix layout: For object with ID: ${impactedObjectId}, please apply: ${repairInstructions}`;
    } else if (rule) {
      targetPrompt = `Fix layout issue ${rule.ruleId || ''}: "${rule.userMessage || ''}". Fix Recommendation: ${repairInstructions}`;
    } else {
      targetPrompt = `Fix layout issue: ${repairInstructions}`;
    }
    
    setPrompt(targetPrompt);
    updateCurrentHistoryEntry(roomData, questionnaire, targetPrompt, model);

    // Scroll to design prompt textarea and focus it
    setTimeout(() => {
      const textarea = document.getElementById('design-prompt-textarea');
      if (textarea) {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }
    }, 50);
  };

  const handleSolveAllErrors = () => {
    if (!validationResult || !validationResult.results) return;

    const failedRules = validationResult.results.filter(r => r.status === 'fail');
    if (failedRules.length === 0) return;

    const instructions = failedRules.map(rule => {
      const impactedObjectId = rule.impactedObjectId;
      const repairInstructions = rule.userRepairInstructions || '';
      const item = impactedObjectId ? roomData.items.find(i => i.itemId === impactedObjectId) : null;

      if (item) {
        return `- For the object "${item.label}" (ID: ${impactedObjectId}): ${repairInstructions}`;
      } else if (impactedObjectId) {
        return `- For object with ID: ${impactedObjectId}: ${repairInstructions}`;
      } else {
        return `- Issue ${rule.ruleId || ''}: "${rule.userMessage || ''}". Fix Recommendation: ${repairInstructions}`;
      }
    }).join('\n');

    const targetPrompt = `Fix the following layout issues:\n${instructions}`;

    setPrompt(targetPrompt);
    updateCurrentHistoryEntry(roomData, questionnaire, targetPrompt, model);

    // Scroll to design prompt textarea and focus it
    setTimeout(() => {
      const textarea = document.getElementById('design-prompt-textarea');
      if (textarea) {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }
    }, 50);
  };

  const handleUpdateLayout = async () => {
    setStatus('loading');
    setErrorMessage('');
    setAiMessage('');
    setLogs([]);
    setGeneratedScript('');
    setValidationResult(null);
    setGeminiCalls([]);
    setSearchLogs([]);

    try {
      const response = await fetch(`${backendUrl}/api/update-layout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roomData,
          questionnaire,
          prompt,
          model
        })
      });

      const resData = await response.json();
      setLogs(resData.logs || []);

      const aiMsg = resData.aiMessage || '';
      setAiMessage(aiMsg);

      if (!response.ok || !resData.success) {
        setStatus('error');
        setErrorMessage(resData.error || 'Server error occurred during layout update.');
        return;
      }

      const script = resData.pythonScript || '';
      const validation = resData.validation || null;
      const calls = resData.geminiCalls || [];
      const sLogs = resData.searchLogs || [];
      
      setGeneratedScript(script);
      setValidationResult(validation);
      setGeminiCalls(calls);
      setSearchLogs(sLogs);
      
      let updatedRoom = roomData;

      if (resData.roomData) {
        updatedRoom = resData.roomData;
      } else if (resData.placements && Array.isArray(resData.placements)) {
        const placementMap = new Map<string, any>(
          resData.placements.map((p: any) => [p.id, p])
        );

        updatedRoom = {
          ...roomData,
          items: roomData.items.map(item => {
            const placed = placementMap.get(item.itemId);
            if (placed) {
              return {
                ...item,
                position: {
                  x: placed.position.x,
                  y: placed.position.y,
                  z: placed.position.z,
                },
                angle: placed.rotation
              };
            }
            return item;
          })
        };
      }
      
      setRoomData(updatedRoom);
      setStatus('success');
      pushToHistory(updatedRoom, questionnaire, prompt, script, validation, calls, resData.logs || [], model, aiMsg, sLogs, zones, zoningExplanation);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : String(err));
    }
  };

  // Generate zones using Gemini spatial reasoning + Discriminator /api/v2/zone
  const handleGenerateZones = async (customRules?: string) => {
    setIsZoning(true);
    setZoningErrorMessage('');
    try {
      const response = await fetch(`${backendUrl}/api/zoning/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          roomData,
          prompt: zoningPrompt,
          questionnaire,
          model: zoningModel,
          customRulesMarkdown: customRules
        })
      });

      const resData = await response.json();
      if (!response.ok || !resData.success) {
        throw new Error(resData.error || 'Zoning generation failed.');
      }

      const generatedZones: Zone[] = resData.zones || [];
      const explanation = resData.aiExplanation || '';
      
      setZones(generatedZones);
      setZoningExplanation(explanation);
      setShowZonesInPreview(true);
      setFocusedZoneId(null);

      const updatedLayout: RoomLayout = resData.updatedRoomData || {
        ...roomData,
        metadata: {
          ...roomData.metadata,
          zones: generatedZones,
          zoningExplanation: explanation
        }
      };

      setRoomData(updatedLayout);
      pushToHistory(
        updatedLayout,
        questionnaire,
        prompt,
        generatedScript,
        validationResult,
        geminiCalls,
        logs,
        model,
        aiMessage,
        searchLogs,
        generatedZones,
        explanation
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setZoningErrorMessage(msg);
    } finally {
      setIsZoning(false);
    }
  };

  const handleClearZones = () => {
    setZones([]);
    setZoningExplanation('');
    setFocusedZoneId(null);
    const cleanedRoom: RoomLayout = {
      ...roomData,
      items: roomData.items.map(item => {
        const meta = { ...(item.metadata || {}) };
        delete meta.zoneId;
        return {
          ...item,
          zoneId: null,
          metadata: meta
        };
      }),
      metadata: {
        ...roomData.metadata,
        zones: [],
        zoningExplanation: ''
      }
    };
    setRoomData(cleanedRoom);
    pushToHistory(
      cleanedRoom,
      questionnaire,
      prompt,
      generatedScript,
      validationResult,
      geminiCalls,
      logs,
      model,
      aiMessage,
      searchLogs,
      [],
      ''
    );
  };

  const handleZoningPromptChange = (newZoningPrompt: string) => {
    setZoningPrompt(newZoningPrompt);
  };

  const handleZoningModelChange = (newZoningModel: string) => {
    setZoningModel(newZoningModel);
  };

  const handleToggleShowZones = (show: boolean) => {
    setShowZonesInPreview(show);
  };

  const handleFocusZone = (zoneId: string | null) => {
    setFocusedZoneId(zoneId);
  };

  return {
    roomData,
    questionnaire,
    prompt,
    model,
    aiMessage,
    backendUrl,
    setBackendUrl,
    status,
    errorMessage,
    logs,
    generatedScript,
    validationResult,
    geminiCalls,
    searchLogs,
    isValidating,
    dbRooms,
    selectedRoomId,
    backendHealthy,
    history,
    historyIndex,
    // Zoning states & methods
    zones,
    isZoning,
    zoningPrompt,
    zoningModel,
    zoningExplanation,
    zoningErrorMessage,
    showZonesInPreview,
    focusedZoneId,
    handleGenerateZones,
    handleClearZones,
    handleZoningPromptChange,
    handleZoningModelChange,
    handleToggleShowZones,
    handleFocusZone,
    // Other handlers
    handleUndo,
    handleRedo,
    handleRoomSelect,
    handleSaveRoom,
    handleFieldChange,
    handleQuestionnaireChange,
    handlePromptChange,
    handleModelChange,
    handleRoomDataUpload,
    handleSolveError,
    handleSolveAllErrors,
    handleUpdateLayout
  };
}
